import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { verfahrenApi } from '../api/verfahren';
import EntityLinkButton from './EntityLinkButton';
import VerfahrenStateBadge from './VerfahrenStateBadge';
import { useIsModuleEnabled } from '../hooks/useConfigVisibility';

interface VerfahrenPickerProps {
  current: { id: number; aktenzeichen: string | null } | null;
  onLinkExisting: (verfahrenId: number) => Promise<unknown>;
  onUnlink: () => Promise<unknown>;
  onCreateAndLink: (data: { aktenzeichen?: string; notes?: string }) => Promise<unknown>;
  /** Content shown in body when linked+view mode (e.g. state suggestion banner) */
  linkedExtra?: React.ReactNode;
  /** Hint text shown when nothing is linked yet */
  unlinkedHint?: string;
}

type Mode = 'view' | 'search' | 'create';

export default function VerfahrenPicker({
  current,
  onLinkExisting,
  onUnlink,
  onCreateAndLink,
  linkedExtra,
  unlinkedHint = 'Noch kein Verfahren zugeordnet.',
}: VerfahrenPickerProps) {
  const navigate = useNavigate();
  const verfahrenVisible = useIsModuleEnabled('verfahren');
  const [mode, setMode] = useState<Mode>('view');
  const [searchText, setSearchText] = useState('');
  const [aktenzeichen, setAktenzeichen] = useState('');
  const [notes, setNotes] = useState('');

  const { data: searchResults } = useQuery({
    queryKey: ['verfahren', 'search', searchText],
    queryFn: () => verfahrenApi.getVerfahren({ search_text: searchText || undefined, per_page: 10 }),
    enabled: mode === 'search',
  });

  const createMutation = useMutation({
    mutationFn: () =>
      onCreateAndLink({
        aktenzeichen: aktenzeichen.trim() || undefined,
        notes: notes.trim() || undefined,
      }),
    onSuccess: () => reset(),
  });

  const handleLinkExisting = async (verfahrenId: number) => {
    await onLinkExisting(verfahrenId);
    reset();
  };

  const reset = () => {
    setMode('view');
    setSearchText('');
    setAktenzeichen('');
    setNotes('');
  };

  const inputClass =
    'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';

  // Body is visible when there is something meaningful to show:
  // - user is actively editing (search or create mode)
  // - nothing is linked yet (show hint + action buttons)
  // - linked with extra content to display (e.g. amber banner)
  const showBody = mode !== 'view' || !current || !!linkedExtra;

  // Verfahren is a feature-gated page; hide the picker entirely when disabled.
  if (!verfahrenVisible) return null;

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <div className="px-6 py-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="text-lg font-medium">Verfahren</h3>
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium ${
              current ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-600'
            }`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${current ? 'bg-purple-500' : 'bg-gray-400'}`} />
            {current ? 'Zugeordnet' : 'Nicht zugeordnet'}
          </span>
          {current && mode === 'view' && (
            <EntityLinkButton
              label={current.aktenzeichen || `Verfahren #${current.id}`}
              onClick={() => navigate(`/verfahren/${current.id}`)}
              color="purple"
            />
          )}
        </div>
        {current && mode === 'view' ? (
          <div className="flex gap-2 shrink-0">
            <button onClick={() => setMode('search')} className="text-sm text-blue-600 hover:text-blue-800">
              Ändern
            </button>
            <button onClick={onUnlink} className="text-sm text-red-600 hover:text-red-800">
              Trennen
            </button>
          </div>
        ) : mode !== 'view' ? (
          <button onClick={reset} className="text-sm text-gray-600 hover:text-gray-800 shrink-0">
            Abbrechen
          </button>
        ) : null}
      </div>

      {/* Body */}
      {showBody && (
        <div className="px-6 py-4">
          {mode === 'search' ? (
            <div className="space-y-3">
              <input
                type="text"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Aktenzeichen oder Notizen suchen..."
                autoFocus
                className={inputClass}
              />
              {searchResults && searchResults.items.length > 0 && (
                <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                  {searchResults.items.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => handleLinkExisting(v.id)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-purple-50 transition-colors flex items-center justify-between gap-2"
                    >
                      <span className="font-medium">{v.aktenzeichen || `Verfahren #${v.id}`}</span>
                      <span className="flex items-center gap-2 shrink-0">
                        <VerfahrenStateBadge staat={v.staat} />
                        <span className="text-xs text-gray-400">{v.inserate_count} Inserate</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              {searchText.length > 0 && searchResults?.items.length === 0 && (
                <p className="text-xs text-gray-400">Kein Verfahren gefunden.</p>
              )}
              <button
                onClick={() => setMode('create')}
                className="text-sm text-purple-600 hover:text-purple-800"
              >
                &laquo;{searchText || 'Neues'}&raquo; eröffnen
              </button>
            </div>
          ) : mode === 'create' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Aktenzeichen</label>
                <input
                  type="text"
                  value={aktenzeichen}
                  onChange={(e) => setAktenzeichen(e.target.value)}
                  placeholder="z.B. AZ-2026-001"
                  autoFocus
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notizen</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Optionale Notizen zum Verfahren..."
                  className={inputClass}
                />
              </div>
              {createMutation.isError && (
                <p className="text-sm text-red-600">Fehler beim Erstellen des Verfahrens.</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => createMutation.mutate()}
                  disabled={createMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {createMutation.isPending ? 'Wird erstellt...' : 'Verfahren eröffnen'}
                </button>
                <button
                  onClick={() => setMode('search')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Zurück
                </button>
              </div>
            </div>
          ) : current ? (
            /* linked + view: only shows if linkedExtra was provided */
            linkedExtra
          ) : (
            /* not linked + view: show hint and action buttons */
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-gray-500">{unlinkedHint}</p>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => setMode('search')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Vorhandenes verknüpfen
                </button>
                <button
                  onClick={() => setMode('create')}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Neues eröffnen
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
